import { Controller } from "@hotwired/stimulus"
import tinymce from "tinymce/tinymce"
import type { Editor } from "tinymce"
import "tinymce/models/dom/model"
import "tinymce/themes/silver/theme"
import "tinymce/icons/default/icons"
// スキンは .js（自己注入版）では Vite 構成で CSS が注入されないため .css を import する。
// エディタ本体の UI は親ドキュメントなので通常の import で当たる。
import "tinymce/skins/ui/oxide/skin.css"
// 本文領域は iframe の中で親の CSS が届かないため、文字列で取り込んで content_style に渡す。
import contentUiCss from "tinymce/skins/ui/oxide/content.css?inline"
import contentCss from "tinymce/skins/content/default/content.css?inline"
import "tinymce/plugins/lists"
import "tinymce/plugins/advlist"
import "tinymce/plugins/link"
import "tinymce/plugins/autolink"
import "tinymce/plugins/charmap"
import "tinymce/plugins/searchreplace"
import "tinymce/plugins/visualblocks"
import "tinymce/plugins/fullscreen"
import "tinymce/plugins/table"
import "tinymce/plugins/wordcount"

interface PreviewResponse {
  subject: string
  body_html: string
  // レジストリに無いキー（誤記・エディタの装飾によるキーの分断）
  unresolved: string[]
  // レジストリに在るがデータが空で、空欄のまま送信されるキー
  blank: string[]
}

const PREVIEW_DEBOUNCE_MS = 500

// iframe 内の本文に当てる基本スタイル。日本語フォントは画面側と揃える。
const EDITOR_CONTENT_STYLE = `
  body {
    font-family: "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif;
    font-size: 14px;
    line-height: 1.7;
  }
`

// メール本文のリッチテキスト編集。変数の置換はサーバ（EmailCampaignContentRenderer）
// だけが行い、ここでは一切置換しない。プレビューは編集内容をそのまま送って返す。
export default class extends Controller<HTMLElement> {
  static targets = [
    "body",
    "subject",
    "previewSubject",
    "previewBody",
    "blankWarning",
    "unresolvedWarning",
    "variableModal",
  ]
  static values = {
    previewUrl: String,
    vacancyId: Number,
    labels: Object,
  }

  declare readonly bodyTarget: HTMLTextAreaElement
  declare readonly subjectTarget: HTMLInputElement
  declare readonly previewSubjectTarget: HTMLElement
  declare readonly previewBodyTarget: HTMLElement
  declare readonly blankWarningTarget: HTMLElement
  declare readonly unresolvedWarningTarget: HTMLElement
  declare readonly variableModalTarget: HTMLElement
  declare readonly hasVariableModalTarget: boolean
  declare readonly previewUrlValue: string
  declare readonly vacancyIdValue: number
  declare readonly labelsValue: Record<string, string>

  private editor: Editor | null = null
  private form: HTMLFormElement | null = null
  private timer: number | undefined
  private requestId = 0

  connect() {
    // レイアウトのヘッダ・サイドバーにログアウト用の form があるため、
    // document.querySelector("form") では本文フォームを掴めない。
    this.form = this.bodyTarget.closest("form")
    this.form?.addEventListener("submit", this.syncBody)
    document.addEventListener("turbo:before-cache", this.teardown)
    document.addEventListener("keydown", this.closeVariableModalOnEscape)

    tinymce.init({
      target: this.bodyTarget,
      license_key: "gpl",
      language: "ja",
      language_url: "/tinymce/langs/ja.js",
      // skin.css を自前で import しているため、URL からの読み込みは止める
      skin: false,
      // content_css は URL から読み込ませず、import した CSS を content_style で注入する
      content_css: false,
      content_style: [ contentUiCss, contentCss, EDITOR_CONTENT_STYLE ].join("\n"),
      entity_encoding: "raw",
      promotion: false,
      menubar: false,
      branding: false,
      // 2カラムのためエディタ幅が 414px 程度になる。既定の 'floating' では
      // ツールバーのボタンが全てオーバーフローの「…」に隠れてしまうので折り返す。
      toolbar_mode: "wrap",
      height: 600,
      plugins:
        "advlist autolink lists link charmap searchreplace visualblocks fullscreen table wordcount",
      toolbar:
        "undo redo | blocks | bold italic underline forecolor backcolor | " +
        "alignleft aligncenter alignright | bullist numlist outdent indent | " +
        "link table charmap | removeformat | searchreplace visualblocks fullscreen",
      setup: (editor: Editor) => {
        this.editor = editor
        editor.on("keyup change input NodeChange", () => this.schedulePreview())
        // 本文は iframe なので、ここで起きた keydown は親ドキュメントに届かない。
        // 親側のリスナーとは別に、同じ処理をエディタ側にも登録する。
        editor.on("keydown", this.closeVariableModalOnEscape)
      },
      init_instance_callback: () => {
        this.runPreview()
      },
    })
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.teardown)
    document.removeEventListener("keydown", this.closeVariableModalOnEscape)
    this.form?.removeEventListener("submit", this.syncBody)
    window.clearTimeout(this.timer)
    this.teardown()
  }

  openVariableModal() {
    this.variableModalTarget.hidden = false
  }

  closeVariableModal() {
    // 破棄の途中でも呼ばれるため、ターゲットが解決できないときは何もしない。
    if (!this.hasVariableModalTarget) return

    this.variableModalTarget.hidden = true
  }

  // 暗い背景（オーバーレイ自身）のクリックでだけ閉じる。中身のクリックでは閉じない。
  closeVariableModalOnBackdrop(event: MouseEvent) {
    if (event.target === this.variableModalTarget) this.closeVariableModal()
  }

  // 連続して複数挿せるよう、挿入してもモーダルは開いたままにする。
  insertVariable(event: Event) {
    const button = event.currentTarget as HTMLElement
    const key = button.dataset.variableKey
    if (!key) return

    if (this.editor) {
      this.editor.insertContent(key)
    } else {
      this.bodyTarget.value += key
    }
    this.schedulePreview()
  }

  schedulePreview() {
    window.clearTimeout(this.timer)
    this.timer = window.setTimeout(() => this.runPreview(), PREVIEW_DEBOUNCE_MS)
  }

  private closeVariableModalOnEscape = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return
    if (!this.hasVariableModalTarget || this.variableModalTarget.hidden) return

    this.closeVariableModal()
  }

  private syncBody = () => {
    if (!this.editor) return

    this.editor.save()
    this.bodyTarget.value = this.editor.getContent()
  }

  private teardown = () => {
    // 開いたままだと hidden なしの DOM が Turbo にキャッシュされ、戻ったとき開いた状態で復帰する。
    this.closeVariableModal()
    if (!this.editor) return

    this.editor.save()
    this.bodyTarget.value = this.editor.getContent()
    tinymce.remove(this.editor)
    this.editor = null
  }

  private async runPreview() {
    const requestId = ++this.requestId
    const body = this.editor ? this.editor.getContent() : this.bodyTarget.value

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({
          vacancy_id: this.vacancyIdValue,
          subject: this.subjectTarget.value,
          body,
        }),
      })
      if (!response.ok) return

      const data = (await response.json()) as PreviewResponse
      // 遅れて届いた古いレスポンスで新しいプレビューを上書きしない
      if (requestId !== this.requestId) return

      this.renderPreview(data)
    } catch {
      // プレビューの失敗は編集を妨げないため、表示を保ったまま黙って諦める
    }
  }

  private renderPreview(data: PreviewResponse) {
    this.previewSubjectTarget.textContent = data.subject
    // サーバでサニタイズ済みの HTML が返る
    this.previewBodyTarget.innerHTML = data.body_html
    this.renderWarning(
      this.blankWarningTarget,
      data.blank,
      "次の項目はデータが未入力のため空欄で送信されます",
    )
    this.renderWarning(
      this.unresolvedWarningTarget,
      data.unresolved,
      "次の変数は定義されていません。誤記かエディタの装飾でキーが分断されている可能性があります",
    )
  }

  private renderWarning(element: HTMLElement, keys: string[], message: string) {
    if (keys.length === 0) {
      element.classList.add("hidden")
      element.textContent = ""
      return
    }

    const names = keys.map((key) => this.labelsValue[key] ?? key)
    element.classList.remove("hidden")
    element.textContent = `${message}：${names.join("、")}`
  }

  private get csrfToken(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
  }
}
